import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const teamGridSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  theme: z.enum(['light', 'dark']).default('light'),
  members: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        role: z.string().max(160),
        bio: z.string().max(500).optional(),
        photo: z.string().min(1).max(1000).optional(),
        linkedin: z.string().url().optional(),
      }),
    )
    .min(1)
    .max(24),
});

export type TeamGridData = z.infer<typeof teamGridSchema>;

export function TeamGridRenderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  theme = 'light',
  members,
}: TeamGridData) {
  const safeMembers = Array.isArray(members) ? members : [];
  const wrapperCls = `v5-section v5-team-section${theme === 'dark' ? ' v5-team-dark' : ''}`;
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className={wrapperCls}>
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-team-h2">{title}</h2>
          {subtitle ? <p className="v5-team-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-team-grid">
          {safeMembers.map((m, i) => (
            <article key={i} className="v5-team-card">
              {m.photo ? (
                <div className="v5-team-photo">
                  <img src={m.photo} alt={m.name} loading="lazy" />
                </div>
              ) : null}
              <div className="v5-team-info">
                <div className="v5-team-name">{m.name}</div>
                {m.role ? <div className="v5-team-role">{m.role}</div> : null}
                {m.bio ? <p className="v5-team-bio">{m.bio}</p> : null}
                {m.linkedin ? (
                  <a className="v5-team-link" href={m.linkedin} target="_blank" rel="noreferrer">
                    LinkedIn →
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export const teamGridBlock: BlockDefinition<TeamGridData> = {
  meta: {
    type: 'team-grid',
    label: 'Equipo / Mentores (v5)',
    description: 'Grid de personas con foto cuadrada, nombre, rol y bio. Variante clara/oscura.',
    category: 'social-proof',
    icon: 'users',
  },
  schema: teamGridSchema,
  defaults: {
    eyebrow: '● Equipo',
    title: 'Selección de la red activa',
    subtitle: 'Una muestra de los profesionales actuales.',
    align: 'left',
    theme: 'dark',
    members: [
      { name: 'Mentor 1', role: 'Ex-CEO · SaaS · 2 exits', bio: 'Especialista en scaling B2B y product-led growth.' },
      { name: 'Mentor 2', role: 'Partner VC · Fintech', bio: '+200 evaluaciones. Lidera la tesis FinTech del fondo.' },
      { name: 'Mentor 3', role: 'Founder · HealthTech', bio: 'Levantó +€20M en 4 rondas. Operador con piel en juego.' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'theme',
      label: 'Tema',
      type: 'select',
      options: [
        { label: 'Claro', value: 'light' },
        { label: 'Oscuro', value: 'dark' },
      ],
    },
    {
      name: 'members',
      label: 'Miembros',
      type: 'array',
      max: 24,
      itemFields: [
        { name: 'name', label: 'Nombre', type: 'text', required: true, max: 120 },
        { name: 'role', label: 'Cargo / Rol', type: 'text', max: 160 },
        { name: 'bio', label: 'Bio', type: 'textarea', max: 500 },
        { name: 'photo', label: 'Foto (URL)', type: 'image' },
        { name: 'linkedin', label: 'LinkedIn', type: 'url' },
      ],
    },
  ],
  Renderer: TeamGridRenderer,
};
